import { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Heart, Share2, MessageCircle, Star } from "lucide-react";
import BottomFooter from "../../components/header-footer/BottomFooter";
import { useAuth } from "../../context/AuthContext";

/**
 * Renders the landing page for the MoZo.Live web application.
 *
 * Displays a responsive layout with a header, hero section, feature highlights, and footer. Includes a username claim form with real-time validation and navigation to the signup page on successful submission. Integrates GDPR compliance banner, and adapts styling for light and dark modes.
 */
export default function Landing() {
  const [username, setUsername] = useState("");
  const [isValid, setIsValid] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Optimize style injection with proper cleanup
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .min-h-screen a:not(.hover\\:text-brand-green):not([class*="text-"]) {
        color: white !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      // Ensure cleanup even if component unmounts unexpectedly
      if (style.parentNode) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const handleUsernameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    // Validate username format (letters, numbers, dots, underscores)
    const usernameRegex = /^[a-zA-Z0-9._]*$/;
    setIsValid(usernameRegex.test(value));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (username && isValid) {
      navigate(`/signup?username=${encodeURIComponent(username)}`);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white dark:bg-black border-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img
                  src="/images/logo-d.svg"
                  alt="MoZo"
                  className="h-8 w-auto hidden sm:block"
                />
                <img
                  src="/images/logo-m.svg"
                  alt="MoZo"
                  className="h-8 w-auto sm:hidden"
                />
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              {!user && (
                <>
                  <Link
                    to="/login"
                    className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-lg text-sm font-medium"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-gray-900 dark:bg-black text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-700 border border-transparent dark:border-transparent"
                  >
                    Sign up
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 flex items-center justify-center min-h-[60vh]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            {/* Content */}
            <div className="flex-1">
              <h1 className="text-4xl sm:text-6xl font-bold text-gray-900 dark:text-white mb-6">
                Making
                <br />
                <span className="text-[#065900] dark:text-[#065900]">
                  Moments
                </span>{" "}
                Matter.
              </h1>
              <p className="text-xl text-white dark:text-white mb-8">
                Share your moments, connect with friends, and discover amazing
                content from creators around the world.
              </p>
              <div className="flex justify-center w-full mb-8">
                <form
                  onSubmit={handleSubmit}
                  className="w-full max-w-md mx-auto"
                >
                  <div className="relative bg-white dark:bg-black rounded-2xl shadow-xl overflow-hidden border-0 transition-all duration-300 hover:shadow-2xl">
                    <div className="flex">
                      <input
                        type="text"
                        placeholder="Claim your username"
                        value={username}
                        onChange={handleUsernameChange}
                        className={`w-full px-5 py-4 bg-transparent focus:outline-none text-white dark:text-white placeholder-white dark:placeholder-white ${!isValid && username ? "border-red-500" : ""
                          }`}
                        required
                      />
                      <button
                        type="submit"
                        disabled={!username || !isValid}
                        className="flex-shrink-0 bg-[#065900] text-white rounded-r-2xl px-6 py-4 font-medium hover:bg-[#065900]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center justify-center"
                      >
                        <span className="hidden sm:inline mr-2">Claim It</span>
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                    {!isValid && username && (
                      <p className="absolute bottom-0 left-0 transform translate-y-full text-xs text-red-500 mt-1 p-1">
                        Only letters, numbers, dots (.) and underscores (_)
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Hero Image */}
            <div className="flex-1 w-full md:w-auto order-first md:order-last">
              <img
                src="/images/hero-d.svg"
                alt="People sharing moments"
                className="w-full h-auto max-h-[350px] object-contain rounded-2xl shadow-xl hidden md:block"
              />
              <img
                src="/images/hero-m.svg"
                alt="People sharing moments"
                className="w-full h-auto max-h-[250px] object-contain rounded-2xl shadow-xl md:hidden"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 dark:from-black dark:to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose <span className="text-brand-green">MoZo</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-white max-w-3xl mx-auto">
              Discover a platform designed to help you connect, share, and grow
              your digital presence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            <div className="bg-white dark:bg-black p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0 group">
              <div className="w-16 h-16 rounded-lg bg-brand-green/10 flex items-center justify-center mb-6 group-hover:bg-brand-green group-hover:text-white transition-colors duration-300">
                <Heart className="w-8 h-8 text-brand-green group-hover:text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-brand-green transition-colors duration-300">
                Share Your Story
              </h3>
              <p className="text-gray-600 dark:text-white">
                Express yourself through photos, videos, and updates that
                capture your unique journey and experiences.
              </p>
              <div className="mt-6 pt-4 border-0">
                <a
                  href="#"
                  className="bg-[#065900] dark:bg-[#065900] text-white dark:text-white font-medium flex items-center px-4 py-2 rounded-lg hover:bg-[#065900]/90 dark:hover:bg-[#065900]/90 transition-colors duration-300"
                >
                  Learn more <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="bg-white dark:bg-black p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0 group">
              <div className="w-16 h-16 rounded-lg bg-brand-green/10 flex items-center justify-center mb-6 group-hover:bg-brand-green group-hover:text-white transition-colors duration-300">
                <MessageCircle className="w-8 h-8 text-brand-green group-hover:text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-brand-green transition-colors duration-300">
                Connect & Chat
              </h3>
              <p className="text-gray-600 dark:text-white">
                Build meaningful relationships with friends and family through
                seamless real-time messaging.
              </p>
              <div className="mt-6 pt-4 border-0">
                <a
                  href="#"
                  className="bg-[#065900] dark:bg-[#065900] text-white dark:text-white font-medium flex items-center px-4 py-2 rounded-lg hover:bg-[#065900]/90 dark:hover:bg-[#065900]/90 transition-colors duration-300"
                >
                  Learn more <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="bg-white dark:bg-black p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0 group">
              <div className="w-16 h-16 rounded-lg bg-brand-green/10 flex items-center justify-center mb-6 group-hover:bg-brand-green group-hover:text-white transition-colors duration-300">
                <Share2 className="w-8 h-8 text-brand-green group-hover:text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-brand-green transition-colors duration-300">
                Discover Content
              </h3>
              <p className="text-gray-600 dark:text-white">
                Explore and engage with content from creators who inspire you
                and share your interests and passions.
              </p>
              <div className="mt-6 pt-4 border-0">
                <a
                  href="#"
                  className="bg-[#065900] dark:bg-[#065900] text-white dark:text-white font-medium flex items-center px-4 py-2 rounded-lg hover:bg-[#065900]/90 dark:hover:bg-[#065900]/90 transition-colors duration-300"
                >
                  Learn more <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="bg-white dark:bg-black p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0 group">
              <div className="w-16 h-16 rounded-lg bg-brand-green/10 flex items-center justify-center mb-6 group-hover:bg-brand-green group-hover:text-white transition-colors duration-300">
                <Star className="w-8 h-8 text-brand-green group-hover:text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-brand-green transition-colors duration-300">
                Build Community
              </h3>
              <p className="text-gray-600 dark:text-white">
                Join and create communities around shared interests, fostering
                connections that extend beyond the digital world.
              </p>
              <div className="mt-6 pt-4 border-0">
                <a
                  href="#"
                  className="bg-[#065900] dark:bg-[#065900] text-white dark:text-white font-medium flex items-center px-4 py-2 rounded-lg hover:bg-[#065900]/90 dark:hover:bg-[#065900]/90 transition-colors duration-300"
                >
                  Learn more <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <a
              href="#"
              className="inline-flex items-center justify-center px-6 py-3 bg-brand-green text-white rounded-lg text-lg font-medium hover:bg-brand-green/90 transition-colors"
            >
              Explore All Features
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-white to-gray-50 dark:from-black dark:to-black">
        <div className="max-w-7xl mx-auto pt-16 pb-8 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between">
            {/* Logo and description */}
            <div className="mb-10 md:mb-0 md:w-1/3">
              <div className="flex items-center mb-4">
                <img
                  src="/images/logo-d.svg"
                  alt="MoZo.Live"
                  className="h-10 w-auto hidden dark:block"
                />
                <img
                  src="/images/logo-l.svg"
                  alt="MoZo.Live"
                  className="h-10 w-auto dark:hidden"
                />
              </div>
              <p className="text-gray-600 dark:text-white mb-6 max-w-sm">
                Share your moments, connect with friends, and discover amazing
                content from creators around the world.
              </p>
              {/* Social Media Links - Vertical on small devices, horizontal on larger screens */}
              <div className="sm:hidden fixed left-4 top-1/2 transform -translate-y-1/2 flex flex-col space-y-4 z-40">
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-brand-green text-white flex items-center justify-center hover:bg-brand-green/90 transition-colors shadow-lg"
                  aria-label="Facebook"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                  </svg>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-brand-green text-white flex items-center justify-center hover:bg-brand-green/90 transition-colors shadow-lg"
                  aria-label="Twitter"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
                  </svg>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-brand-green text-white flex items-center justify-center hover:bg-brand-green/90 transition-colors shadow-lg"
                  aria-label="Instagram"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="2"
                      y="2"
                      width="20"
                      height="20"
                      rx="5"
                      ry="5"
                    ></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
              </div>

              {/* Social Media Links - Horizontal for larger screens */}
              <div className="hidden sm:flex space-x-4">
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-brand-green text-white flex items-center justify-center hover:bg-brand-green/90 transition-colors"
                  aria-label="Facebook"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                  </svg>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-brand-green text-white flex items-center justify-center hover:bg-brand-green/90 transition-colors"
                  aria-label="Twitter"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
                  </svg>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-brand-green text-white flex items-center justify-center hover:bg-brand-green/90 transition-colors"
                  aria-label="Instagram"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="2"
                      y="2"
                      width="20"
                      height="20"
                      rx="5"
                      ry="5"
                    ></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
              </div>
            </div>
            {/* Links */}
            <div className="grid grid-cols-2 md:grid-cols-2 gap-8 md:gap-12 md:w-2/3 justify-items-end">
              <div>
                <h3 className="text-sm font-bold text-gray-400 dark:text-white uppercase tracking-wider mb-4">
                  Company
                </h3>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/company/about"
                      className="text-gray-600 dark:text-gray-300 hover:text-brand-green dark:hover:text-brand-green transition-colors"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      About
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/company/blog"
                      className="text-gray-600 dark:text-gray-300 hover:text-brand-green dark:hover:text-brand-green transition-colors"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/company/careers"
                      className="text-gray-600 dark:text-gray-300 hover:text-brand-green dark:hover:text-brand-green transition-colors"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      Careers
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-400 dark:text-white uppercase tracking-wider mb-4">
                  Creators
                </h3>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/creators-hub?tab=creators&creator=guidelines"
                      className="text-gray-600 dark:text-gray-300 hover:text-brand-green dark:hover:text-brand-green transition-colors"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      Creator Guidelines
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/creators-hub?tab=legal&legal=fair-use"
                      className="text-gray-600 dark:text-gray-300 hover:text-brand-green dark:hover:text-brand-green transition-colors"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      Fair Use Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/creators-hub?tab=legal&legal=dmca"
                      className="text-gray-600 dark:text-gray-300 hover:text-brand-green dark:hover:text-brand-green transition-colors"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      DMCA
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Main footer content ends here */}
        </div>
      </footer>
      <BottomFooter />
    </div>
  );
}
